import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';
import { type TFunction, useTranslation } from 'react-i18next';

import { type ClassRecord, type ClassSession, type ClassStudent } from './types';
import {
    type AttendanceMap,
    type AttendanceMarkPayload,
    type AttendanceTotals,
    type ClassAttendanceSummary,
    type ClassData
} from '../components/attendance/types';
import { CLASS_ATTENDANCE, CLASS_INFO, CLASS_SESSIONS, CLASS_STUDENTS } from '../config/apiPaths';
import { getComparator } from '../utils/sortingUtils';
import { getUrls } from '../utils/url';

export enum View {
    Session,
    Student,

    SessionSummary,
    StudentSummary
}

export enum AttendanceError {
    ERR_NO_RESPONSE = 1000,
    ERR_REQUEST = 1001
}

export interface AttendanceProviderProps {
    classId: string;

    studentId?: string;
    classSessionId?: string;

    view?: View;
}

interface AttendanceContextInterface {
    loading: boolean;
    error?: number;

    classUuid: string;
    classInfo: ClassRecord;

    students: ClassStudent[];
    sessions: ClassSession[];

    sessionAttendance: AttendanceMap | null;
    studentAttendance: AttendanceMap | null;
    classAttendance: ClassAttendanceSummary;

    selectedStudent?: string;
    selectedSession?: string;

    studentId?: string;
    classSessionId?: string;

    view: View;
    markAttendance: (payload: AttendanceMarkPayload) => void;

    totals: AttendanceTotals;
    setTotals: (totals: AttendanceTotals) => void;
    progress: number;

    setStudentId: (studentId: string) => void;
    setClassSessionId: (classSessionId: string) => void;

    setSelectedStudent: (studentId: string) => void;
    setSelectedSession: (classSessionId: string) => void;
    fetchSessions: () => void;
    fetchSummary: () => void;

    attendanceCache: { [key: string]: any };
    t: TFunction<'attendance'>;
}

const SESSION_GENERATE_BATCH_SIZE = 10;

const AttendanceContext = createContext(undefined as unknown as AttendanceContextInterface);

const { Provider, Consumer } = AttendanceContext;

const AttendanceProvider: React.FC<AttendanceProviderProps> = (props) => {
    const { t } = useTranslation('attendance');

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<number | undefined>(undefined);

    const [progress, setProgress] = useState(0);

    const [studentId, setStudentId] = useState<string | undefined>(props.studentId);
    const [classSessionId, setClassSessionId] = useState<string | undefined>(props.classSessionId);

    const [selectedStudent, setSelectedStudent] = useState<string | undefined>(undefined);
    const [selectedSession, setSelectedSession] = useState<string | undefined>(undefined);

    const [, setMarkedData] = useState<any>({ error: undefined, loading: false });
    const [classData, setClassData] = useState<ClassData>({
        error: undefined,
        loading: true,
        sessions: [],
        students: [],
        class: { school_name: '', class_name: '', meeting_id: '', sid: 0 }
    });

    const [sessionAttendance, setSessionAttendance] = useState<AttendanceMap | null>(null);
    const [studentAttendance, setStudentAttendance] = useState<AttendanceMap | null>(null);
    const [classAttendance, setClassAttendance] = useState<ClassAttendanceSummary>([]);

    const [view, setView] = useState<View>(View.Session);
    const [totals, setTotals] = useState<AttendanceTotals>({ present: 0, absent: 0, tardy: 0 });

    const attendanceCache = useRef({});
    const { api } = getUrls();

    const isDemo = useMemo(() => {
        const search = new URLSearchParams(window.location.search);
        return search.get('isDemo');
    }, []);

    const getErrorCode = (err: AxiosError): number => {
        if (err.response) {
            return err.response.status;
        }

        if (err.request) {
            return AttendanceError.ERR_NO_RESPONSE;
        }

        return AttendanceError.ERR_REQUEST;
    };

    const setViewBasedAttendance = (viewSwitch, studentState, sessionState) => {
        switch (viewSwitch) {
            case View.Student:
                setStudentAttendance(studentState);
                break;
            case View.Session:
                setSessionAttendance(sessionState);
                break;
            default:
                break;
        }
    };

    /**
     * Mark attendance for a student and
     * optimistically update state
     */
    const markAttendance = useCallback(
        (payload: AttendanceMarkPayload) => {
            const markStudentAttendance = async () => {
                const studentSnapshot = { ...studentAttendance };
                const sessionSnapshot = { ...sessionAttendance };
                const cacheSnapshot = { ...attendanceCache.current };

                const updatedMarks = {
                    marked_present: payload.present,
                    marked_tardy: payload.tardy,
                    marked_join_time: payload.join_time,
                    marked_leave_time: payload.leave_time
                };

                /**
                 * Update the student and session attendance caches if
                 * they have preloaded data to reflect our changes
                 */
                payload.student_ids.forEach((id) => {
                    if (attendanceCache.current[payload.session_id]) {
                        attendanceCache.current[payload.session_id] = {
                            ...attendanceCache.current[payload.session_id],
                            [id]: { marks: updatedMarks }
                        };
                    }

                    if (attendanceCache.current[id]) {
                        attendanceCache.current[id] = {
                            ...attendanceCache.current[id],
                            [payload.session_id]: { marks: updatedMarks }
                        };
                    }
                });

                /**
                 * Immediately & optimistically update
                 * attendance table based on the view
                 */
                setViewBasedAttendance(
                    view,
                    attendanceCache.current[payload.student_ids[0]],
                    attendanceCache.current[payload.session_id]
                );

                Object.keys(payload).forEach((prop) => {
                    if (payload[prop] === null) delete payload[prop];
                });

                /**
                 * Execute background API request for persistence
                 */
                try {
                    const { data: updatedAttendance } = await axios.post(`${api}${CLASS_ATTENDANCE}`, payload);

                    // update cache
                    Object.keys(updatedAttendance).forEach((id) => {
                        if (attendanceCache.current[payload.session_id]) {
                            attendanceCache.current[payload.session_id] = {
                                ...attendanceCache.current[payload.session_id],
                                [id]: { marks: updatedAttendance[id] }
                            };
                        }

                        if (attendanceCache.current[id]) {
                            attendanceCache.current[id] = {
                                ...attendanceCache.current[id],
                                [payload.session_id]: { marks: updatedAttendance[id] }
                            };
                        }
                    });

                    /**
                     * Update the table for the 2nd time, this time reflecting the server response
                     */
                    setViewBasedAttendance(
                        view,
                        attendanceCache.current[payload.student_ids[0]],
                        attendanceCache.current[payload.session_id]
                    );
                } catch (err) {
                    setMarkedData({ loading: false, error: getErrorCode(err as AxiosError) });

                    attendanceCache.current = cacheSnapshot;

                    setViewBasedAttendance(view, studentSnapshot, sessionSnapshot);
                }
            };

            markStudentAttendance();
        },
        [api, view, sessionAttendance, studentAttendance]
    );

    /**
     * Fetch the summary of all students for all
     * class sessions and fill the cache
     */
    const fetchSummary = useCallback(async () => {
        if (classAttendance.length > 0) return;

        const { sessions } = classData;

        setIsLoading(true);

        try {
            let completedBatches = 0;
            const summary: ClassAttendanceSummary = [];
            const totalBatches = Math.ceil(sessions.length / SESSION_GENERATE_BATCH_SIZE);

            const updateProgress = (batchIndex: number) => {
                const progressPercentage = Math.ceil((batchIndex / totalBatches) * 100);
                setProgress(progressPercentage);
            };

            /**
             * Batch get all session attendance reports
             */
            for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
                const startIndex = batchIndex * SESSION_GENERATE_BATCH_SIZE;
                const endIndex = Math.min(startIndex + SESSION_GENERATE_BATCH_SIZE, sessions.length);

                const batchSessions = sessions.slice(startIndex, endIndex);

                const promises = batchSessions.map(async (session) => {
                    const response = await axios(`${api}${CLASS_ATTENDANCE}/session?session_id=${session.session_id}`);
                    const report = {
                        session_id: session.session_id,
                        students: response.data
                    };

                    // Fill the entire cache!
                    attendanceCache.current[report.session_id] = report.students;

                    Object.keys(report.students).forEach((id) => {
                        if (!attendanceCache.current[id]) {
                            attendanceCache.current[id] = {};
                        }
                        attendanceCache.current[id][report.session_id] = report.students[id];
                    });

                    summary.push(report);
                });

                // eslint-disable-next-line no-await-in-loop
                await Promise.all(promises);

                completedBatches += 1;
                updateProgress(completedBatches);
            }

            setClassAttendance(summary);

            setIsLoading(false);
        } catch (err) {
            setError(getErrorCode(err as AxiosError));
            setIsLoading(false);
        }
    }, [classAttendance.length, classData, api]);

    /**
     * Fetch attendance reports for all sessions
     * for a student.
     */
    useEffect(() => {
        const fetchStudentAttendance = async () => {
            if (!studentId) return;

            if (studentId === 'student-summary') {
                setClassSessionId(undefined);

                await fetchSummary();
                setView(View.StudentSummary);
                return;
            }

            let attendanceResult = attendanceCache.current[studentId];

            if (!attendanceResult) {
                setIsLoading(true);

                try {
                    const config: AxiosRequestConfig = {
                        params: {
                            student_id: studentId
                        }
                    };

                    const response = await axios(`${api}${CLASS_ATTENDANCE}/student`, config);
                    attendanceResult = response.data;
                    attendanceCache.current[studentId] = attendanceResult;
                } catch (err) {
                    setError(getErrorCode(err as AxiosError));
                    setIsLoading(false);
                    return;
                }
            }

            setStudentAttendance(attendanceResult);
            setClassSessionId(undefined);

            setView(View.Student);
            setIsLoading(false);
        };

        fetchStudentAttendance();
    }, [studentId, api, fetchSummary, isDemo, props.classId]);

    /**
     * Fetch attendance reports for all the students
     * in a session.
     */
    useEffect(() => {
        const fetchSessionAttendance = async () => {
            if (!classSessionId) return;
            if (!classData.sessions.length) return;

            if (classSessionId === 'session-summary') {
                setStudentId(undefined);

                await fetchSummary();
                setView(View.SessionSummary);
                return;
            }

            let attendanceResult = attendanceCache.current[classSessionId];

            if (classSessionId === 'today') {
                if (classData.sessions[0]) {
                    setClassSessionId(classData.sessions[0].session_id);
                }

                return;
            }

            if (!attendanceResult && !isDemo) {
                setIsLoading(true);

                try {
                    const response = await axios(`${api}${CLASS_ATTENDANCE}/session?session_id=${classSessionId}`);
                    attendanceResult = response.data;
                    attendanceCache.current[classSessionId] = attendanceResult;
                } catch (err) {
                    setError(getErrorCode(err as AxiosError));
                    setIsLoading(false);
                    return;
                }
            }

            setSessionAttendance(attendanceResult);
            setStudentId(undefined);

            setView(View.Session);
            setIsLoading(false);
        };

        fetchSessionAttendance();
    }, [classSessionId, api, classData.sessions, fetchSummary, isDemo]);

    /**
     * Fetch class students and sessions to
     * populate selector for attendance view
     */
    useEffect(() => {
        const fetchClassData = async () => {
            try {
                const students = axios(`${api}${CLASS_STUDENTS}?class_id=${props.classId}`);
                const sessions = axios(`${api}${CLASS_SESSIONS}?class_id=${props.classId}`);
                const classInfo = axios(`${api}${CLASS_INFO}?class_id=${props.classId}&with_info=true`);

                const [studentResult, sessionResult, classResult] = await Promise.all([students, sessions, classInfo]);

                const now = new Date();

                const currentStudents = studentResult.data;
                const pastSessions = sessionResult.data.filter((i) => new Date(i.start_time) < now);
                const sortedSessions = pastSessions.sort(getComparator('desc', 'start_time'));
                const onlyStudents = currentStudents.filter((user) => user.role.includes('student'));

                setClassData({
                    error: undefined,
                    loading: false,
                    students: onlyStudents,
                    sessions: sortedSessions,
                    class: classResult.data
                });
                setIsLoading(false);
            } catch (err) {
                setIsLoading(false);

                setClassData({
                    loading: false,
                    error: getErrorCode(err as AxiosError),
                    students: [],
                    sessions: [],
                    class: { school_name: '', class_name: '', meeting_id: '', sid: 0 }
                });
            }
        };

        fetchClassData();
    }, [api, props.classId]);

    useEffect(() => {
        if (isDemo) {
            // Allows for consistent demo data across different views
            fetchSummary();
        }
    }, [fetchSummary, isDemo]);

    /**
     * Fetch sessions and flush the cache
     */
    const fetchSessions = useCallback(async () => {
        setClassData({ ...classData, loading: true });

        try {
            const sessions = await axios(`${api}${CLASS_SESSIONS}?class_id=${props.classId}`);

            const pastSessions = sessions.data.filter((i) => new Date(i.start_time) < new Date());
            const sortedSessions = pastSessions.sort(getComparator('desc', 'start_time'));

            attendanceCache.current = {};

            setClassData({ ...classData, sessions: sortedSessions, loading: false });
        } catch (err) {
            setClassData({ ...classData, error: getErrorCode(err as AxiosError), loading: false });
        }
    }, [api, classData, props.classId]);

    return (
        <Provider
            value={{
                loading: classData.loading || isLoading,
                error: classData.error || error,

                classUuid: props.classId,
                classInfo: classData.class,

                students: classData.students,
                sessions: classData.sessions,

                sessionAttendance,
                studentAttendance,
                classAttendance,

                selectedStudent,
                selectedSession,

                studentId,
                classSessionId,

                view,
                markAttendance,

                totals,
                setTotals,
                progress,

                setStudentId,
                setClassSessionId,

                setSelectedStudent,
                setSelectedSession,
                fetchSessions,
                fetchSummary,

                attendanceCache,
                t
            }}
        >
            {props.children}
        </Provider>
    );
};

export { AttendanceContext, AttendanceProvider, Consumer as AttendanceConsumer };
